import { useState, useEffect } from 'react';
import { useProfile, type ChartTheme } from '@/hooks/useProfile';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Palette } from 'lucide-react';

const chartThemes: Record<ChartTheme, { name: string; color: string }> = {
  coral: { name: 'Coral (Por defecto)', color: 'hsl(14, 78%, 55%)' },
  ocean: { name: 'Océano', color: 'hsl(200, 80%, 50%)' },
  forest: { name: 'Bosque', color: 'hsl(142, 71%, 45%)' },
  purple: { name: 'Púrpura', color: 'hsl(270, 70%, 55%)' },
  sunset: { name: 'Atardecer', color: 'hsl(35, 90%, 55%)' },
};

export function ChartThemeSettings() {
  const { profile, isLoading, updateProfile } = useProfile();
  const [theme, setTheme] = useState<ChartTheme>('coral');

  useEffect(() => {
    if (profile?.chart_theme) {
      setTheme(profile.chart_theme);
    }
  }, [profile?.chart_theme]);

  const handleSave = () => {
    updateProfile.mutate({ chart_theme: theme });
  };

  if (isLoading) {
    return <div className="text-muted-foreground">Cargando...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5" />
          Estilo de Gráficos
        </CardTitle>
        <CardDescription>
          Personaliza los colores de los gráficos en reportes y dashboards
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="chart-theme">Tema de Color</Label>
          <Select value={theme} onValueChange={(val) => setTheme(val as ChartTheme)}>
            <SelectTrigger id="chart-theme" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(chartThemes).map(([key, { name, color }]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded-full border border-border" 
                      style={{ backgroundColor: color }}
                    />
                    <span>{name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Preview */}
        <div className="p-4 rounded-lg bg-muted/50 space-y-2">
          <p className="text-sm text-muted-foreground">Vista previa</p>
          <div className="h-4 rounded-full overflow-hidden bg-muted">
            <div 
              className="h-full rounded-full transition-all duration-300"
              style={{ 
                backgroundColor: chartThemes[theme].color,
                width: '65%'
              }}
            />
          </div>
        </div>

        <Button 
          onClick={handleSave} 
          disabled={updateProfile.isPending || theme === profile?.chart_theme}
        >
          {updateProfile.isPending ? 'Guardando...' : 'Guardar'}
        </Button>
      </CardContent>
    </Card>
  );
}